import React, { useState, useEffect, useCallback, useContext, useMemo } from 'react';
import sortBy from 'lodash/sortBy';

import { Grid, Typography, FormControl, InputLabel, Select, MenuItem, FormHelperText, Box, TextField, Autocomplete, IconButton } from '@mui/material';
import { SelectChangeEvent } from '@mui/material/Select';
import { alpha } from '@mui/material/styles';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

import { DISC_BAG_NAMES } from '../data/disc_data';
import { Course, CourseConfiguration, Hole } from '../data_types/courseTypes';
import { shortNameToCourse, sceneNameToHole, shortNameIdToConfigName } from '../data/coursesLoader';
import { EventRound, generateRandomWindSeed } from '../data_types/types';
import { DataContext } from '../data/DataContext';
import { DataContextType } from '../data_types/interfaces';

import { PlainObject } from '../data_types/types';
import { DS, Overline } from '../shared/Dashboard';

// Assuming your course and hole data are already available
// import { HOLE_PREFIX_TO_DATA, HOLE_TO_SCENE } from '../data/holes'; // Import the course and hole data


interface EventRoundDetailsFormProps {
    formData: {
        rounds: EventRound[];
    };
    handleRoundChange: (index: number, updatedRound: EventRound) => void;
    handleInputChange: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent | { value: unknown }) => void;
    onValidationChange: (isValid: PlainObject) => void;
}

interface RoundErrors {
    courseSelection: boolean;
    customCourseName: boolean;
    holes: boolean[];
}

const generateLabelFromSceneName = (sceneName: string): string => {
    const hole = sceneNameToHole[sceneName];
    if (!hole) {
        console.warn(`No hole found for sceneName: ${sceneName}`);
        return 'Unknown Hole (Par: ?)';
    }

    const shortNameId = hole.shortNameId;
    const configName = shortNameIdToConfigName[shortNameId];
    const hasUnderscore = shortNameId.includes('_');

    if (hasUnderscore && configName) {
        // Extract the base shortNameId by removing the suffix after '_'
        const baseShortNameId = shortNameId.split('_')[0];
        return `${baseShortNameId} ${configName} (Par: ${hole.par})`;
    }

    return `${shortNameId} (Par: ${hole.par})`;
};

const EventRoundDetailsForm: React.FC<EventRoundDetailsFormProps> = ({ formData, handleRoundChange, handleInputChange, onValidationChange }) => {
    const context = useContext<DataContextType | undefined>(DataContext);
    const { courses, loadingCourses } = context || {};

    const [numRounds, setNumRounds] = useState(formData.rounds.length || 1);
    const [errors, setErrors] = useState<RoundErrors[]>([]);
    const [lockOption, setLockOption] = useState<string>('unlockAll');
    const [selectedRound, setActiveRound] = useState<number>(0);

    const constructCourseName = (course: Course, configuration: CourseConfiguration): string => {
        const isMultipleConfigs = course.configurations.length > 1;
        let name = course.name;
        if (isMultipleConfigs) {
            name += ` ${configuration.name}`;
        }
        return name;
    };

    // sceneNameToHole is filled once courses finish loading
    const options = useMemo(() => Object.keys(sceneNameToHole), [courses]);

    // Initialize rounds to 4 on component mount
    useEffect(() => {
        if (formData.rounds.length === 0) {
            const initialRounds = Array.from({ length: 4 }).map(() => ({
                courseID: '',
                courseName: '',
                discBag: 0,
                holes: [],
                locked: false,
                reverse: false,
                windSeed: generateRandomWindSeed(),
                customCourse: false,
            }));

            setNumRounds(4);

            const customEvent = {
                target: {
                    name: 'rounds',
                    value: initialRounds,
                },
            } as unknown as React.ChangeEvent<HTMLInputElement>;
            handleInputChange(customEvent);
        } else {
            setNumRounds(formData.rounds.length);
        }
    }, [formData.rounds, handleInputChange]);

    // Validate form
    useEffect(() => {
        const newErrors: RoundErrors[] = formData.rounds.slice(0, numRounds).map((round) => ({
            courseSelection: !round.customCourse && round.courseID === '',
            customCourseName: round.customCourse ? round.courseName.trim() === '' : false,
            holes: round.customCourse ? Array.from({ length: 9 }).map((_, holeIndex) => !round.holes[holeIndex]?.holeName) : [],
        }));

        setErrors(newErrors);
        onValidationChange({ rounds: newErrors });

    }, [formData.rounds, numRounds, onValidationChange]);

    // Handle the change of number of rounds
    const handleNumberOfRoundsChange = (event: SelectChangeEvent<number>) => {
        const selectedRounds = Number(event.target.value);
        setNumRounds(selectedRounds);
        const updatedRounds = [...formData.rounds];

        if (selectedRounds > updatedRounds.length) {
            while (updatedRounds.length < selectedRounds) {
                updatedRounds.push({
                    courseID: '',
                    courseName: '',
                    discBag: 0,
                    holes: [],
                    locked: false,
                    reverse: false,
                    windSeed: generateRandomWindSeed(),
                    customCourse: false,
                });
            }
        } else if (selectedRounds < updatedRounds.length) {
            updatedRounds.splice(selectedRounds);
        }

        const customEvent = {
            target: {
                name: 'rounds',
                value: updatedRounds,
            },
        } as unknown as React.ChangeEvent<HTMLInputElement>;

        handleInputChange(customEvent);
    };

    const handleLockOptionChange = (event: SelectChangeEvent<string>) => {
        const selectedOption = event.target.value;
        setLockOption(selectedOption);

        const updatedRounds = formData.rounds.map((round, index) => {
            let locked = false;
            if (selectedOption === 'lockAll') {
                locked = true;
            } else if (selectedOption === 'unlockAll') {
                locked = false;
            } else if (selectedOption === 'unlockFirst') {
                locked = index !== 0;
            }
            return {
                ...round,
                locked,
            };
        });

        const customEvent = {
            target: {
                name: 'rounds',
                value: updatedRounds,
            },
        } as unknown as React.ChangeEvent<HTMLInputElement>;

        handleInputChange(customEvent);
    };

    // Handle the selection of a course
    const handleCourseSelection = useCallback((roundIndex: number, event: SelectChangeEvent<string>) => {
        const selectedOption = event.target.value;
        if (selectedOption === 'custom') {
            // Create updated round object for custom course
            const updatedRound: EventRound = {
                ...formData.rounds[roundIndex],
                customCourse: true,
                courseID: '',
                courseName: '',
                holes: Array.from({ length: 9 }).map(() => ({
                    holeName: '',
                    pS: 0,
                    par: 3,
                })),
            };
            handleRoundChange(roundIndex, updatedRound);
            return;
        }

        // Parse the selected option to extract shortName, configId, and portion
        const [shortName, configIdStr, portion] = selectedOption.split('-');
        const configId = Number(configIdStr);

        // Retrieve the course from the context
        const course: Course | undefined = shortNameToCourse[shortName];
        if (!course) {
            console.error(`Course data for '${shortName}' not found.`);
            return;
        }

        // Find the appropriate configuration based on configId and portion
        const configuration: CourseConfiguration | undefined = course.configurations.find(config =>
            config.configId === configId && config.portion === (portion === 'Default' ? undefined : portion)
        );
        if (!configuration) {
            console.error(`Configuration with ID '${configId}' and portion '${portion}' for course '${shortName}' not found.`);
            return;
        }

        // Construct the courseName
        let courseName = constructCourseName(course, configuration);

        // Create updated round object for selected course
        const updatedRound: EventRound = {
            ...formData.rounds[roundIndex],
            customCourse: false,
            courseID: selectedOption,
            courseName: courseName,
            holes: configuration.holes.map((hole: Hole) => ({
                holeName: hole.sceneName,
                pS: 0,
                par: hole.par,
            })),
        };
        handleRoundChange(roundIndex, updatedRound);
    }, [formData.rounds, handleRoundChange]);

    // Handle the selection of individual holes for custom courses
    const handleHoleSelection = useCallback(
        (roundIndex: number, holeIndex: number, newValue: string) => {
            const updatedHoles = [...formData.rounds[roundIndex].holes];
            if (newValue) {
                // Removed unused variable 'hole'
                const holeDetails = sceneNameToHole[newValue];
                if (!holeDetails) {
                    console.error(`Hole with sceneName '${newValue}' not found.`);
                    return;
                }
                updatedHoles[holeIndex] = {
                    ...updatedHoles[holeIndex],
                    holeName: holeDetails.sceneName, // Store sceneName
                    par: holeDetails.par, // Optionally store par
                };
            } else {
                // Handle clearing the selection
                updatedHoles[holeIndex] = {
                    ...updatedHoles[holeIndex],
                    holeName: '',
                    par: 3, // Reset to default or desired value
                };
            }

            const updatedRound: EventRound = {
                ...formData.rounds[roundIndex],
                holes: updatedHoles,
            };
            handleRoundChange(roundIndex, updatedRound);
        },
        [formData.rounds, handleRoundChange]
    );

    // Handle the change of disc bag
    const handleBagSelection = (index: number, event: SelectChangeEvent<number>) => {
        const selectedBag = Number(event.target.value);
        const updatedRound: EventRound = {
            ...formData.rounds[index],
            discBag: selectedBag,
        };
        handleRoundChange(index, updatedRound);
    };

    // Handle the change of custom course name
    const handleCustomCourseName = (index: number, event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const customCourseName = event.target.value;
        const updatedRound: EventRound = {
            ...formData.rounds[index],
            courseName: customCourseName,
        };
        handleRoundChange(index, updatedRound);
    };

    if (loadingCourses) {
        return <Typography sx={{ fontSize: '14px', color: DS.textMuted }}>Loading courses...</Typography>;
    }

    const allCourses = sortBy(courses, 'name') || [];
    const sortedBagNames = sortBy(DISC_BAG_NAMES, 'label');

    const visibleRounds = formData.rounds.slice(0, numRounds);
    const activeRound = Math.min(selectedRound, Math.max(visibleRounds.length - 1, 0));
    const activeRoundData = visibleRounds[activeRound];

    const getBagLabel = (bagValue: number) =>
        DISC_BAG_NAMES.find((bag) => bag.value === bagValue)?.label ?? 'Default';
    const roundPar = (round: EventRound) =>
        (round.holes || []).reduce((sum, hole) => sum + (Number(hole?.par) || 0), 0);
    const roundHasError = (index: number) =>
        !!errors[index]?.courseSelection || !!errors[index]?.customCourseName || !!errors[index]?.holes.some(Boolean);

    return (
        <Grid container spacing={2}>
            {/* Number of Rounds Selector */}
            <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                    <InputLabel id="num-rounds-label">Number of Rounds</InputLabel>
                    <Select
                        labelId="num-rounds-label"
                        id="num-rounds"
                        value={numRounds}
                        onChange={handleNumberOfRoundsChange}
                        label="Number of Rounds"
                    >
                        {[1, 2, 3, 4].map((num) => (
                            <MenuItem key={num} value={num}>
                                {num}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Grid>

            {/* Round Locking Options */}
            <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                    <InputLabel id="lock-rounds-label">Lock Rounds</InputLabel>
                    <Select
                        labelId="lock-rounds-label"
                        id="lock-rounds"
                        value={lockOption}
                        onChange={handleLockOptionChange}
                        label="Lock Rounds"
                    >
                        <MenuItem value="lockAll">Lock all rounds</MenuItem>
                        <MenuItem value="unlockAll">Unlock all rounds</MenuItem>
                        <MenuItem value="unlockFirst">Unlock first round</MenuItem>
                    </Select>
                </FormControl>
            </Grid>

            {/* Round overview: one compact tile per round */}
            <Grid item xs={12}>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: `repeat(${Math.max(visibleRounds.length, 1)}, 1fr)` }, gap: 1.5 }}>
                    {visibleRounds.map((round, index) => {
                        const selected = index === activeRound;
                        const hasError = roundHasError(index);
                        const par = roundPar(round);
                        const pickedHoles = (round.holes || []).filter((hole) => hole?.holeName).length;
                        const courseLabel = round.customCourse
                            ? (round.courseName || 'Custom course')
                            : (round.courseName || 'No course selected');

                        return (
                            <Box
                                key={index}
                                role="tab"
                                aria-selected={selected}
                                onClick={() => setActiveRound(index)}
                                sx={{
                                    minWidth: 0,
                                    p: 1.5,
                                    borderRadius: '12px',
                                    cursor: 'pointer',
                                    background: selected ? alpha(DS.brand, 0.08) : DS.inset,
                                    border: `1px solid ${hasError ? alpha(DS.loss, 0.45) : selected ? alpha(DS.brand, 0.5) : DS.border}`,
                                    transition: 'all 0.15s ease',
                                    '&:hover': { borderColor: hasError ? alpha(DS.loss, 0.6) : alpha(DS.brand, 0.35) },
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 0.75 }}>
                                    <Overline color={selected ? DS.brand : DS.textMuted}>Round {index + 1}</Overline>
                                    {round.locked
                                        ? <LockIcon sx={{ fontSize: 14, color: DS.warning }} />
                                        : <LockOpenIcon sx={{ fontSize: 14, color: DS.gain }} />}
                                </Box>
                                <Typography noWrap title={courseLabel} sx={{
                                    fontSize: '14px',
                                    fontWeight: 700,
                                    color: round.courseName ? DS.text : DS.textFaint,
                                }}>
                                    {courseLabel}
                                </Typography>
                                <Typography noWrap sx={{ fontSize: '12px', color: DS.textMuted, mt: 0.25 }}>
                                    {getBagLabel(round.discBag)}
                                </Typography>
                                <Typography noWrap sx={{ fontSize: '12px', color: hasError ? DS.loss : DS.textFaint, mt: 0.25, fontVariantNumeric: 'tabular-nums' }}>
                                    {hasError
                                        ? (round.customCourse ? `${pickedHoles}/9 holes · needs attention` : 'Needs a course')
                                        : `${(round.holes || []).length} holes · par ${par}`}
                                </Typography>
                            </Box>
                        );
                    })}
                </Box>
            </Grid>

            {/* Editor for the selected round */}
            {activeRoundData && (() => {
                const index = activeRound;
                const round = activeRoundData;

                return (
                    <Grid item xs={12}>
                        <Box sx={{
                            p: { xs: 2, sm: 2.5 },
                            borderRadius: '12px',
                            background: DS.inset,
                            border: `1px solid ${roundHasError(index) ? alpha(DS.loss, 0.35) : DS.border}`,
                        }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 2 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                    <Typography sx={{ fontSize: '15px', fontWeight: 800, color: DS.text }}>
                                        Editing round {index + 1}
                                    </Typography>
                                    <Box component="span" sx={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: 0.5,
                                        px: 1,
                                        py: '2px',
                                        borderRadius: '999px',
                                        fontSize: '12px',
                                        fontWeight: 700,
                                        background: alpha(round.locked ? DS.warning : DS.gain, 0.14),
                                        color: round.locked ? DS.warning : DS.gain,
                                    }}>
                                        {round.locked ? <LockIcon sx={{ fontSize: 12 }} /> : <LockOpenIcon sx={{ fontSize: 12 }} />}
                                        {round.locked ? 'Locked' : 'Open'}
                                    </Box>
                                </Box>
                                <Box sx={{ display: 'flex', gap: 0.5 }}>
                                    <IconButton
                                        size="small"
                                        aria-label="Previous round"
                                        disabled={index === 0}
                                        onClick={() => setActiveRound(index - 1)}
                                        sx={{ color: DS.textMuted }}
                                    >
                                        <ChevronLeftIcon fontSize="small" />
                                    </IconButton>
                                    <IconButton
                                        size="small"
                                        aria-label="Next round"
                                        disabled={index >= visibleRounds.length - 1}
                                        onClick={() => setActiveRound(index + 1)}
                                        sx={{ color: DS.textMuted }}
                                    >
                                        <ChevronRightIcon fontSize="small" />
                                    </IconButton>
                                </Box>
                            </Box>

                            <Grid container spacing={2}>
                                {/* Course Selector */}
                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth size="small" error={errors[index]?.courseSelection}>
                                        <InputLabel id={`round-${index}-course-label`}>Select Course</InputLabel>
                                        <Select
                                            labelId={`round-${index}-course-label`}
                                            id={`round-${index}-course`}
                                            value={round.customCourse ? 'custom' : round.courseID}
                                            onChange={(event) => {
                                                handleCourseSelection(index, event);
                                            }}
                                            label="Select Course"
                                            required
                                        >
                                            {/* Placeholder MenuItem */}
                                            <MenuItem value="" disabled>
                                                Select Course
                                            </MenuItem>
                                            <MenuItem value="custom">Custom Course</MenuItem>
                                            {allCourses.map((course) => (
                                                course.configurations.map((config) => (
                                                    <MenuItem
                                                        key={`${course.shortName}-${config.configId}-${config.portion || 'Default'}`}
                                                        value={`${course.shortName}-${config.configId}-${config.portion || 'Default'}`}
                                                    >
                                                        {constructCourseName(course, config)}
                                                    </MenuItem>
                                                ))
                                            ))}
                                        </Select>
                                        {errors[index]?.courseSelection && (
                                            <FormHelperText>Please select a course.</FormHelperText>
                                        )}
                                    </FormControl>
                                </Grid>

                                {/* Disc Bag Selector */}
                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel id={`round-${index}-bag-label`}>Select Bag</InputLabel>
                                        <Select
                                            labelId={`round-${index}-bag-label`}
                                            id={`round-${index}-bag`}
                                            value={round.discBag}
                                            onChange={(event) => handleBagSelection(index, event as SelectChangeEvent<number>)}
                                            label="Select Bag"
                                        >
                                            {sortedBagNames.map((bag) => (
                                                <MenuItem key={bag.value} value={bag.value}>
                                                    {bag.label}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>

                                {/* Custom Course Name Input */}
                                {round.customCourse && (
                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            label="Custom Course Name"
                                            value={round.courseName}
                                            onChange={(event) => handleCustomCourseName(index, event)}
                                            error={errors[index]?.customCourseName}
                                            helperText={
                                                errors[index]?.customCourseName
                                                    ? 'Layout name is required'
                                                    : ''
                                            }
                                            required
                                            size="small"
                                        />
                                    </Grid>
                                )}

                                {/* Holes of a preset course (read only) */}
                                {!round.customCourse && round.holes?.length > 0 && (
                                    <Grid item xs={12}>
                                        <Typography sx={{ fontSize: '12px', color: DS.textMuted }}>
                                            {round.holes.length} holes · par {roundPar(round)}
                                        </Typography>
                                    </Grid>
                                )}

                                {/* Holes of a custom course: one per row, in play order */}
                                {round.customCourse && (
                                    <Grid item xs={12}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                                            <Overline>Holes</Overline>
                                            <Typography sx={{ fontSize: '12px', color: DS.textMuted, fontVariantNumeric: 'tabular-nums' }}>
                                                par {roundPar(round)}
                                            </Typography>
                                        </Box>
                                        <Grid container spacing={1.5}>
                                            {
                                                // Render editable dropdowns for custom courses
                                                Array.from({ length: 9 }).map((_, holeIdx) => {
                                                    const sceneName = round.holes[holeIdx]?.holeName || '';

                                                    return (
                                                        <Grid item xs={12} key={holeIdx}>
                                                            <Autocomplete
                                                                options={options}
                                                                getOptionLabel={(option) => generateLabelFromSceneName(option)}
                                                                isOptionEqualToValue={(option, value) => option === value}
                                                                renderOption={(props, option) => (
                                                                    <li
                                                                        {...props}
                                                                        style={{
                                                                            whiteSpace: 'nowrap',
                                                                            overflow: 'hidden',
                                                                            textOverflow: 'ellipsis',
                                                                        }}
                                                                        key={option}
                                                                    >
                                                                        {generateLabelFromSceneName(option)}
                                                                    </li>
                                                                )}
                                                                value={sceneName || null}
                                                                onChange={(event, newValue) =>
                                                                    handleHoleSelection(index, holeIdx, newValue || '')
                                                                }
                                                                renderInput={(params) => (
                                                                    <TextField
                                                                        {...params}
                                                                        label={`Hole ${holeIdx + 1}`}
                                                                        placeholder="Select hole"
                                                                        error={!!errors[index]?.holes[holeIdx]}
                                                                        helperText={
                                                                            errors[index]?.holes[holeIdx]
                                                                                ? 'Hole selection is required.'
                                                                                : ''
                                                                        }
                                                                        required
                                                                        size="small"
                                                                    />
                                                                )}
                                                                freeSolo={false}
                                                            />
                                                        </Grid>
                                                    );
                                                })
                                            }
                                        </Grid>
                                    </Grid>
                                )}
                            </Grid>
                        </Box>
                    </Grid>
                );
            })()}
        </Grid>
    );
};

export default EventRoundDetailsForm;
